import { useCallback, useMemo, useState } from "react";
import { IRowNode } from "ag-grid-community";
import { toast } from "react-toastify";
import {
  AgGrid,
  Button,
  Card,
  Flex,
  Grid,
  QueryLoader,
  Text,
  type AgGridReact,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useQueryParam } from "~/common/hooks/useQueryParams";
import {
  useGetProfilesForCaseQuery,
  useReconcileProfileHierarchyMutation,
} from "~/graphql/supergraphHooks";
import { OwnershipPercentCellEditor } from "../../CaseDetailsDrawer/Profiles/ProfilesTreeCard/OwnershipPercentCellEditor";
import { RoleCellEditor } from "../../CaseDetailsDrawer/Profiles/ProfilesTreeCard/RoleCellEditor";
import { useProfileTreeData } from "../../CaseDetailsDrawer/Profiles/ProfilesTreeCard/useProfileTreeData";
import { isOwnerRole } from "../../CaseDetailsDrawer/Profiles/ProfilesTreeCard/utils";
import { OwnersAndControllersRowItem } from "../../CaseDetailsDrawer/Profiles/types";
import { getInvestingProfileName as getProfileName } from "../../CaseDetailsDrawer/Profiles/utils";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import { useIOReusableProfiles } from "../../flags";
import * as styles from "./styles_vanilla.css";
import { formatOwnershipDisplay, parseOwnershipToNumber } from "./utils";
import type { ProfileHierarchyRowInput } from "~/graphql/supergraphTypes";

export const ProfileHierarchyTable = ({
  onCancel,
  onComplete,
}: {
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const [profId] = useQueryParam("profileId");
  const profileId = Number(profId);
  const {
    caseId,
    programId,
    editHierarchySupported: enableDragAndDrop,
  } = useCurrentCaseContext();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const profileDetailsResults = useGetProfilesForCaseQuery({
    variables: {
      caseId: caseId,
      ioReusableProfilesEnabled,
    },
    fetchPolicy: "network-only",
  });
  const complianceCase = useMemo(
    () => profileDetailsResults.data?.complianceCase,
    [profileDetailsResults.data?.complianceCase],
  );
  const entityName = useMemo(
    () => getProfileName(complianceCase?.investingProfile),
    [complianceCase?.investingProfile],
  );
  const profileName = useMemo(() => {
    const profile = complianceCase?.profiles.find((profile) => profile?.id === profileId);
    if (!profile) return entityName;
    return getProfileName(profile);
  }, [complianceCase?.profiles, entityName, profileId]);
  const [gridApi, setGridApi] = useState<AgGrid.GridApi<OwnersAndControllersRowItem> | null>(
    null,
  );

  const [reconcileHierarchy, { loading: isSaving }] = useReconcileProfileHierarchyMutation({
    refetchQueries: ["GetProfilesForCase", "GetComplianceCase"],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast.success("Hierarchy saved successfully");
      onComplete();
    },
    onError: (e) => toast.error(e.message),
  });

  const { rowData: rawRowData, gridOptions } = useProfileTreeData({
    complianceCase,
    caseId,
    programId,
    readonly: false,
    showNestedOwnership: false,
    showScreeningColumns: false,
    enableRowDrag: enableDragAndDrop,
  });

  const rowData = rawRowData ?? [];
  const hasNonRootProfiles = rowData.some((row) => !row.isInvestorRoot);

  const saveHierarchy = useCallback(async () => {
    if (!hasNonRootProfiles) {
      onComplete();
      return;
    }

    gridApi?.stopEditing(false);

    const hierarchyRows: ProfileHierarchyRowInput[] = [];
    gridApi?.forEachNode((node: IRowNode<OwnersAndControllersRowItem>) => {
      if (node.data) {
        const rawOwnership = node.data.ownership;
        const parsedOwnership = parseOwnershipToNumber(rawOwnership);
        const ownership = parsedOwnership == null ? undefined : `${parsedOwnership}%`;

        hierarchyRows.push({
          id: node.data.id,
          parentId: node.data.parentId,
          relationship: node.data.relationship,
          ownership,
          name: node.data.name,
        });
      }
    });

    await reconcileHierarchy({
      variables: {
        hierarchyRows,
        caseId,
      },
    });
  }, [caseId, gridApi, hasNonRootProfiles, onComplete, reconcileHierarchy]);

  const cellRenderers = {
    profileNameRenderer: ({ data }: { data: OwnersAndControllersRowItem }) => (
      <Text variant="strong-md" className={styles.nameCell}>
        {data?.name}
      </Text>
    ),
    roleRenderer: ({ data }: { data: OwnersAndControllersRowItem }) => (
      <Text variant="regular-md">
        {data?.isInvestorRoot ? "Root profile" : (data?.relationship ?? "--")}
      </Text>
    ),
    ownershipRenderer: ({ data }: { data: OwnersAndControllersRowItem }) => (
      <Text variant="regular-md">{formatOwnershipDisplay(data?.ownership)}</Text>
    ),
  };

  const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "relationship",
      headerName: "Role",
      flex: 1,
      minWidth: 120,
      cellRenderer: "roleRenderer",
      cellEditor: RoleCellEditor,
      cellEditorPopup: true,
      editable: (params: { data?: OwnersAndControllersRowItem }) =>
        !params.data?.isInvestorRoot,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      },
    },
    {
      field: "ownership",
      headerName: "Ownership %",
      flex: 1,
      minWidth: 120,
      cellRenderer: "ownershipRenderer",
      cellEditor: OwnershipPercentCellEditor,
      editable: (params: { data?: OwnersAndControllersRowItem }) =>
        !params.data?.isInvestorRoot && isOwnerRole(params.data?.relationship),
      valueGetter: (params: { data?: OwnersAndControllersRowItem }) =>
        parseOwnershipToNumber(params.data?.ownership),
      valueSetter: (params: {
        data?: OwnersAndControllersRowItem;
        newValue?: number | null;
      }): boolean => {
        if (params.data) {
          const n = params.newValue;
          params.data.ownership = n != null && Number.isFinite(n) ? `${n}%` : "--";
        }
        return true;
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      },
    },
  ];

  const gridOptionsWithFlex = useMemo(() => {
    if (!gridOptions) return gridOptions;
    return {
      ...gridOptions,
      autoGroupColumnDef: {
        ...gridOptions.autoGroupColumnDef,
        flex: 1,
        minWidth: 200,
      },
      onFirstDataRendered: (event: { api: { sizeColumnsToFit: () => void } }) => {
        event.api.sizeColumnsToFit();
      },
    };
  }, [gridOptions]);

  return (
    <QueryLoader result={profileDetailsResults}>
      <div className={styles.container}>
        <Card className={styles.card}>
          <div
            className={styles.backLink}
            onClick={onCancel}
            onKeyDown={(e) => e.key === "Enter" && onCancel()}
            role="button"
            tabIndex={0}
          >
            <Icon type="arrow-left" />
            <Text variant="regular-md">{profileName}</Text>
          </div>

          <div className={styles.headerSection}>
            <Text variant="strong-xl">Profile hierarchy</Text>
            <Text variant="regular-md" style={{ color: Colors.Neutral600 }}>
              Define the profile hierarchy, roles and ownership percentages of {entityName}
            </Text>
          </div>

          <div className={styles.tableWrapper}>
            <Grid
              className={styles.profilesGrid}
              gridOptions={gridOptionsWithFlex}
              rowData={rowData}
              columnDefs={columnDefs}
              components={cellRenderers}
              onGridReady={(event) => setGridApi(event.api)}
            />
          </div>

          <div className={styles.infoBox}>
            <Flex gap={4}>
              <Text variant="strong-md">Owners</Text>
              <Text variant="regular-md">
                are individuals or entities who own 25% or more of the equity interests of the
                entity.
              </Text>
            </Flex>
            <Flex gap={4}>
              <Text variant="strong-md">Controllers</Text>
              <Text variant="regular-md">
                are individuals or entities with significant responsibility for managing the
                entity.
              </Text>
            </Flex>
            <Text variant="regular-md">
              If this profile is neither an owner or a controller, do not select either role.
            </Text>
          </div>
        </Card>

        <div className={styles.footerSection}>
          <Button variant="neutral" onClick={onCancel} disabled={isSaving}>
            Back
          </Button>
          <Button variant="primary" onClick={() => void saveHierarchy()} isLoading={isSaving}>
            Save profile
          </Button>
        </div>
      </div>
    </QueryLoader>
  );
};
